import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { GeneralError, issueFor, PRICING_KEY } from './PricingLayout';
import { bpToPct, pctToBp, schemePreview } from './units';
import { NumberInput } from '../ui/NumberInput';
import { sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';

export type DayScheme = { id: string; name: string; pctsBp: number[]; isDefault: boolean; system: boolean; version: number };

const MAX_DAYS = 31;

/** "1. dzień (%)", "2. dzień (%)", the last of several: "3. i kolejne dni (%)". */
const dayLabel = (i: number, count: number) => (count > 1 && i === count - 1 ? `${i + 1}. i kolejne dni (%)` : `${i + 1}. dzień (%)`);

const draftOf = (s?: DayScheme) => ({ name: s?.name ?? '', pcts: (s?.pctsBp ?? [10000]).map(bpToPct) });

/**
 * Add (no `scheme`) or edit a day scheme: the percentages of the 1st, 2nd, "3rd and further" days with a live
 * preview computed by the same function the quotes use. On edit only changed fields are sent.
 */
export function DaySchemeEditor({ scheme, onClose }: { scheme?: DayScheme; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [base, setBase] = useState(scheme);
  const [draft, setDraft] = useState(draftOf(scheme));
  const parsed = draft.pcts.map(pctToBp);
  const valid = parsed.every((p): p is number => p !== null);

  const save = useMutation({
    mutationFn: () => {
      const pctsBp = parsed as number[];
      const original = draftOf(base);
      const nameChanged = !base || draft.name !== original.name;
      const pctsChanged = !base || pctsBp.join(',') !== base.pctsBp.join(',');
      const fields = { ...(nameChanged ? { name: draft.name } : {}), ...(pctsChanged ? { pctsBp } : {}) };
      return base
        ? api<DayScheme>(`/api/pricing/day-schemes/${base.id}`, { method: 'PATCH', body: { version: base.version, ...fields } })
        : api<DayScheme>('/api/pricing/day-schemes', { method: 'POST', body: fields });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRICING_KEY });
      onClose();
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  function reload() {
    const current = (save.error as ApiError).current as DayScheme | undefined;
    if (current) {
      setBase(current);
      setDraft(draftOf(current));
    }
    save.reset();
    void queryClient.invalidateQueries({ queryKey: PRICING_KEY });
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) save.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(draft, draftOf(base)),
    save: async (el) => {
      if (!valid || !validForm(el)) return false;
      await save.mutateAsync();
      return true;
    },
  });

  const setPct = (i: number, v: string) => setDraft({ ...draft, pcts: draft.pcts.map((p, j) => (j === i ? v : p)) });
  const nameError = issueFor(save.error, 'name');
  const pctsError = issueFor(save.error, 'pctsBp');

  return (
    <form ref={guardRef} className="panel form" aria-label={base ? `Edycja: ${base.name}` : 'Nowy schemat dni'} data-feature-id="pricing.day_schemes" onSubmit={submit}>
      <h2>{base ? `Schemat „${base.name}”` : 'Nowy schemat dni'}</h2>
      {conflict && <ConflictNotice onReload={reload} />}
      <GeneralError error={save.error} fields={['name', 'pctsBp']} />
      <label className="field">
        <span>Nazwa</span>
        <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required autoComplete="off" />
        {nameError && <span className="field-error">{nameError}</span>}
      </label>
      <fieldset className="scheme-days">
        <legend>Procent ceny za dobę</legend>
        {draft.pcts.map((p, i) => (
          <div key={i} className="scheme-day">
            <label className="field">
              <span>{dayLabel(i, draft.pcts.length)}</span>
              <NumberInput value={p} onValueChange={(v) => setPct(i, v)} aria-invalid={parsed[i] === null} />
              {parsed[i] === null && <span className="field-error">Wpisz procent od 0 do 1000</span>}
            </label>
            {draft.pcts.length > 1 && (
              <button className="btn small" type="button" aria-label={`Usuń dzień ${i + 1}`} onClick={() => setDraft({ ...draft, pcts: draft.pcts.filter((_, j) => j !== i) })}>
                Usuń
              </button>
            )}
          </div>
        ))}
        {pctsError && <span className="field-error">{pctsError}</span>}
        {draft.pcts.length < MAX_DAYS && (
          <div className="actions">
            <button className="btn small" type="button" onClick={() => setDraft({ ...draft, pcts: [...draft.pcts, draft.pcts.at(-1) ?? '100'] })}>
              Dodaj dzień
            </button>
          </div>
        )}
        <p className="muted">Ostatni procent obowiązuje dla każdego dalszego dnia.</p>
      </fieldset>
      <p className="scheme-preview" role="status" aria-label="Podgląd">
        {valid ? schemePreview(parsed as number[]) : 'Popraw procenty, żeby zobaczyć podgląd.'}
      </p>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending || !valid}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj schemat'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
